/* @jsx h */
import { computed, defineComponent, h, ref } from "vue";
import { LemmonModal, useLocalization } from "@lemmonhq/uikit";
import {
  requestCreateLoginToken,
  signInWithGoogleCredential,
  verifyLoginCode,
} from "@app/server/client";
import { useCustomer } from "../../providers/auth-context";
import { CodeStage } from "./CodeStage";
import { EmailStage } from "./EmailStage";
import { TermsNotice } from "./TermsNotice";
import type { GoogleOneTapCredential } from "./googleOneTap";
import type { AuthenticationComponentProps, AuthenticationMode } from "./types";

export type { AuthenticationState } from "./types";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const AuthenticationComponent =
  defineComponent<AuthenticationComponentProps>({
    name: "AuthenticationComponent",
    props: ["state", "onClose"],
    setup(props) {
      const { t } = useLocalization();
      const loading = ref(false);
      const error = ref<string | null>(
        props.state.value.oauthFailed === "apple"
          ? t("customer.account.apple_signin_failed")
          : props.state.value.oauthFailed === "google"
            ? t("customer.account.google_signin_failed")
            : null,
      );
      const resent = ref(false);
      const mode = ref<AuthenticationMode>("signup");
      const email = ref("");
      const name = ref("");
      const birthMonth = ref("");
      const birthDay = ref("");
      const birthYear = ref("");
      const code = ref("");
      const auth = useCustomer();
      const currentYear = new Date().getFullYear();

      const isEmailStage = computed(() => !props.state.value.email);
      const isSignup = computed(() => mode.value === "signup");
      const birthDayMonth = computed<string | null>(() => {
        if (!birthMonth.value || !birthDay.value) return null;
        return `${birthMonth.value.padStart(2, "0")}-${birthDay.value.padStart(2, "0")}`;
      });
      const canSubmitEmail = computed(() => {
        const emailValid = EMAIL_RE.test(email.value.trim());
        return isSignup.value
          ? emailValid && name.value.trim().length > 0
          : emailValid;
      });
      const canVerify = computed(() => code.value.trim().length >= 4);
      const title = computed(() =>
        isEmailStage.value
          ? isSignup.value
            ? t("customer.account.create_account")
            : t("customer.account.sign_in")
          : t("customer.account.code_title"),
      );

      const setMode = (next: AuthenticationMode) => {
        mode.value = next;
        error.value = null;
      };

      const sendCode = async (isResend = false) => {
        loading.value = true;
        error.value = null;
        try {
          const year = parseInt(birthYear.value, 10);
          await requestCreateLoginToken(
            email.value.trim(),
            isSignup.value
              ? {
                  name: name.value.trim(),
                  birthDayMonth: birthDayMonth.value,
                  birthYear:
                    birthDayMonth.value && year >= 1900 && year <= currentYear
                      ? year
                      : null,
                }
              : undefined,
          );
          if (isResend) {
            resent.value = true;
            setTimeout(() => (resent.value = false), 2500);
          } else {
            props.state.value.email = email.value.trim();
          }
        } catch (err: any) {
          error.value =
            err?.message || t("customer.account.auth_generic_error");
        } finally {
          loading.value = false;
        }
      };

      const verifyCode = async () => {
        loading.value = true;
        error.value = null;
        try {
          await verifyLoginCode(props.state.value.email!, code.value.trim());
          props.state.value.code = code.value;
          await auth.refresh();
          props.onClose();
        } catch (err: any) {
          error.value = err?.message || t("customer.account.invalid_code");
        } finally {
          loading.value = false;
        }
      };

      const signInWithGoogle = async ({
        credential,
        state,
      }: GoogleOneTapCredential) => {
        loading.value = true;
        error.value = null;
        try {
          await signInWithGoogleCredential(credential, state);
          await auth.refresh();
          props.onClose();
        } catch {
          error.value = t("customer.account.google_signin_failed");
        } finally {
          loading.value = false;
        }
      };

      const backToEmail = () => {
        props.state.value.email = undefined;
        code.value = "";
        error.value = null;
      };

      return () => (
        <LemmonModal
          title={title.value}
          style="z-index: 60;"
          disableBottomSheet={false}
          mobileVariant="full-screen"
          onClose={props.onClose}
          renderButtons={() => (
            <div class="border-t border-base-300 p-4">
              <button
                class="btn btn-primary btn-block btn-lg rounded-xl"
                disabled={
                  loading.value ||
                  (isEmailStage.value
                    ? !canSubmitEmail.value
                    : !canVerify.value)
                }
                onClick={() => (isEmailStage.value ? sendCode() : verifyCode())}
              >
                {loading.value && (
                  <span class="loading loading-spinner loading-sm" />
                )}
                {isEmailStage.value
                  ? isSignup.value
                    ? t("customer.account.create_account")
                    : t("customer.account.send_code")
                  : t("customer.account.verify_continue")}
              </button>
            </div>
          )}
        >
          <div class="space-y-4 p-4">
            {isEmailStage.value ? (
              <EmailStage
                mode={mode.value}
                email={email.value}
                name={name.value}
                birthMonth={birthMonth.value}
                birthDay={birthDay.value}
                birthYear={birthYear.value}
                onModeChange={setMode}
                onEmailChange={(value) => (email.value = value)}
                onNameChange={(value) => (name.value = value)}
                onBirthMonthChange={(value) => (birthMonth.value = value)}
                onBirthDayChange={(value) => (birthDay.value = value)}
                onBirthYearChange={(value) => (birthYear.value = value)}
                onGoogleCredential={signInWithGoogle}
              />
            ) : (
              <CodeStage
                email={props.state.value.email!}
                code={code.value}
                loading={loading.value}
                resent={resent.value}
                onCodeChange={(value) => (code.value = value)}
                onChangeEmail={backToEmail}
                onResend={() => sendCode(true)}
              />
            )}

            {error.value && (
              <div class="flex items-start gap-2 rounded-xl border border-error/20 bg-error/5 p-3 text-sm text-error">
                <span
                  class="material-symbols-outlined text-base"
                  aria-hidden="true"
                >
                  error_outline
                </span>
                <span class="flex-1">{error.value}</span>
              </div>
            )}

            {isEmailStage.value && <TermsNotice mode={mode.value} />}
          </div>
        </LemmonModal>
      );
    },
  });
